import React, { useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getNotificationNumber } from '../../../../shared/functions/Functions';
import { OffersFilterModal } from './OffersFilterModal';
import { IOfferManagementFilter } from '../dto/IOfferManagementFilter';
import { OfferFilterSort } from './OfferFilterSort';
import { useNavigate } from 'react-router-dom';
import { BsSliders } from 'react-icons/bs';
import { InternalRoutes } from '../../../../enum/InternalRoutes';
import { BiGift } from 'react-icons/bi';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../store/rootReducer';
import { SearchInput } from '../../../../shared/components/SearchInput';

interface IOffersFilterProps {
    offerFilter: IOfferManagementFilter;
    setOfferFilter: React.Dispatch<React.SetStateAction<IOfferManagementFilter>>;
    newDeclined?: boolean;
    isAgency?: boolean;
    isFilterLoading?: boolean;
}

export function OffersFilter({
    offerFilter,
    setOfferFilter,
    isAgency,
    newDeclined,
    isFilterLoading = false,
}: IOffersFilterProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [show, setShow] = useState(false);
    const { redDots } = useSelector((state: IAllStates) => state.redDots);

    const closeModal = () => {
        setShow(false);
    };

    const handleSearch = (value: string) => {
        setOfferFilter(prevState => ({ ...prevState, name: value }));
    };

    const changeBarterStatus = () => {
        if (isFilterLoading) return;
        setOfferFilter(prevState => ({ ...prevState, isCharity: !prevState.isCharity }));
    };

    const changeContractStatus = () => {
        if (isFilterLoading) return;
        setOfferFilter(prevState => ({ ...prevState, isContract: !prevState.isContract }));
    };

    const goToPlanner = () => {
        navigate(isAgency ? '/agency/planner' : '/influencer/planner');
    };

    const declinedOfferAction = () => {
        navigate(isAgency ? InternalRoutes.AGENCY_DECLINED_OFFER : '/influencer/offer/declined');
    };

    const loadingClass = isFilterLoading ? 'opacity-50' : '';

    return (
        <>
            <OffersFilterModal
                show={show}
                closeModal={closeModal}
                statusBarterHandle={changeBarterStatus}
                offerFilter={offerFilter}
                onSearch={handleSearch}
                isAgency={isAgency}
                statusContractHandle={changeContractStatus}
                isFilterLoading={isFilterLoading}
            />
            <Row className="d-none d-md-flex align-items-center mb-4">
                <Col md={8} className="d-flex align-items-center">
                    <SearchInput
                        name="title"
                        defaultValue={offerFilter?.name ?? ''}
                        onSearch={handleSearch}
                        isLoading={isFilterLoading}
                        label={t('general.button.search')}
                        className="w-md-25"
                    />
                    <div
                        onClick={changeBarterStatus}
                        className={`influencer-tab-filter primary-dark my-3 ml-md-3 my-md-0 position-relative ${
                            offerFilter?.isCharity ? 'active' : ''
                        } ${loadingClass}`}
                    >
                        <BiGift size={18} />
                        <span className="ml-1">
                            {!offerFilter?.isCharity ? t('influencer.explore.barter') : t('influencer.explore.paid')}
                        </span>
                        {!offerFilter?.isCharity
                            ? !!getNotificationNumber('barterOffers', redDots) && (
                                  <span className="sidebar-bullet">
                                      {getNotificationNumber('barterOffers', redDots)}
                                  </span>
                              )
                            : !!getNotificationNumber('paidOffers', redDots) && (
                                  <span className="sidebar-bullet">{getNotificationNumber('paidOffers', redDots)}</span>
                              )}
                    </div>
                    <div
                        onClick={declinedOfferAction}
                        className="influencer-tab-filter danger my-3 ml-md-3 my-md-0 position-relative"
                    >
                        {t('brand.campaign.book.offer.filters.declined')}
                        {newDeclined && <span style={{ right: -5 }} className="notification-bullet-sm"></span>}
                    </div>
                    <div
                        onClick={changeContractStatus}
                        className={`influencer-tab-filter primary my-3 ml-md-3 my-md-0 position-relative ${
                            offerFilter?.isContract ? 'active' : ''
                        } ${loadingClass}`}
                    >
                        {t('brand.campaign.book.offer.filters.long_term')}
                    </div>
                </Col>
                <Col className="text-end d-flex justify-content-end">
                    <OfferFilterSort offerFilter={offerFilter} setOfferFilter={setOfferFilter} />
                    <Button onClick={goToPlanner} variant="outline-primary" size="sm">
                        {t('general.button.plan_my_camp')}
                    </Button>
                </Col>
            </Row>
            <div className="d-flex d-md-none align-items-center justify-content-between mb-2">
                <Button variant={!show ? 'outline-secondary' : 'primary'} onClick={() => setShow(true)}>
                    <BsSliders size={20} />
                </Button>
                <OfferFilterSort offerFilter={offerFilter} setOfferFilter={setOfferFilter} />
            </div>
        </>
    );
}
